import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import type { ComponentChildren } from 'preact';

interface Props {
  contextBar?: ComponentChildren;
  keyboardStripOpen: boolean;
  onToggleKeyboard: () => void;
  soundOn: boolean;
  onToggleSound: () => void;
  showSound: boolean;
  /** Content rendered as a floating panel when keyboard is toggled */
  keyboardContent?: ComponentChildren;
  /** SSH connection state — shows 🖥/⏏ button in expanded items */
  sshConnected?: boolean;
  onSshConnect?: () => void;
  onSshDisconnect?: () => void;
}

export function FloatingActionCluster({
  contextBar,
  keyboardStripOpen,
  onToggleKeyboard,
  soundOn,
  onToggleSound,
  showSound,
  keyboardContent,
  sshConnected,
  onSshConnect,
  onSshDisconnect,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const [scrollFaded, setScrollFaded] = useState(false);
  const collapseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clusterRef = useRef<HTMLDivElement>(null);
  // Ref mirrors expanded so event handlers always read current value without stale closures
  const expandedRef = useRef(false);
  useEffect(() => { expandedRef.current = expanded; }, [expanded]);

  // Collapse helper — always fires 'fab-cluster-collapsed' so the keyboard strip closes too
  const collapse = useCallback(() => {
    setExpanded(false);
    document.dispatchEvent(new CustomEvent('fab-cluster-collapsed'));
  }, []);

  // Auto-collapse after 8 seconds (was 4s — too short to reach keyboard strip → 🧠)
  useEffect(() => {
    if (expanded) {
      collapseTimer.current = setTimeout(() => collapse(), 8000);
      return () => { if (collapseTimer.current) clearTimeout(collapseTimer.current); };
    }
  }, [expanded, collapse]);

  // Reset collapse timer on any interaction inside expanded cluster
  const resetTimer = useCallback(() => {
    if (collapseTimer.current) clearTimeout(collapseTimer.current);
    collapseTimer.current = setTimeout(() => collapse(), 8000);
  }, [collapse]);

  // Scroll-aware fade — same pattern as other FABs (back, play, compose)
  useEffect(() => {
    const chatArea = document.querySelector('.chat-area');
    if (!chatArea) return;
    let fadeTimeout: ReturnType<typeof setTimeout> | null = null;
    const fade = (duration: number) => {
      setScrollFaded(true);
      if (fadeTimeout) clearTimeout(fadeTimeout);
      fadeTimeout = setTimeout(() => setScrollFaded(false), duration);
    };
    const onScroll = () => fade(600);
    const onTap = (e: Event) => {
      if (clusterRef.current && clusterRef.current.contains(e.target as Node)) return;
      if (expandedRef.current) {
        // Tap outside collapses the expanded cluster
        if (collapseTimer.current) clearTimeout(collapseTimer.current);
        collapse();
      } else {
        // Tap fades the cluster (same behaviour as other FABs)
        fade(2000);
      }
    };
    chatArea.addEventListener('scroll', onScroll, { passive: true });
    chatArea.addEventListener('pointerdown', onTap, { passive: true });
    return () => {
      chatArea.removeEventListener('scroll', onScroll);
      chatArea.removeEventListener('pointerdown', onTap);
      if (fadeTimeout) clearTimeout(fadeTimeout);
    };
  }, []);

  // Click outside (non-chat-area) to collapse — chat area taps are owned by onTap above
  useEffect(() => {
    if (!expanded) return;
    const chatArea = document.querySelector('.chat-area');
    const onClick = (e: MouseEvent) => {
      if (clusterRef.current && !clusterRef.current.contains(e.target as Node)) {
        if (chatArea && chatArea.contains(e.target as Node)) return; // let onTap handle it
        collapse();
      }
    };
    document.addEventListener('pointerdown', onClick, true);
    return () => document.removeEventListener('pointerdown', onClick, true);
  }, [expanded]);

  const handleSshClick = () => {
    // Defer state updates to avoid Preact re-render mid-pointer-event-sequence → freeze.
    // Same pattern as speed badge fix (PR #190).
    if (sshConnected) {
      setTimeout(() => { onSshDisconnect?.(); collapse(); }, 0);
    } else {
      setTimeout(() => { onSshConnect?.(); collapse(); }, 0);
    }
  };

  const showSsh = onSshConnect !== undefined || onSshDisconnect !== undefined;

  return (
    <div
      ref={clusterRef}
      class={`fab-cluster${expanded ? ' expanded' : ''}${scrollFaded ? ' scroll-faded' : ''}`}
    >
      {/* Expanded fan-out items */}
      {expanded && (
        <div class="fab-cluster-items" onPointerDown={resetTimer}>
          {contextBar && (
            <div class="fab-cluster-item fab-cluster-item--context">
              {contextBar}
            </div>
          )}
          <button
            type="button"
            class={`fab-cluster-item utb-icon-btn${keyboardStripOpen ? ' on' : ''}`}
            onClick={() => { onToggleKeyboard(); resetTimer(); }}
            title="Quick access toolbar"
            aria-label="Quick access toolbar"
          >&#9000;</button>
          {showSound && (
            <button
              type="button"
              class={`fab-cluster-item utb-icon-btn${soundOn ? ' on' : ''}`}
              onClick={() => { onToggleSound(); resetTimer(); }}
              title={soundOn ? 'Mute' : 'Unmute'}
              aria-label={soundOn ? 'Mute' : 'Unmute'}
            >{soundOn ? '\u{1F50A}' : '\u{1F507}'}</button>
          )}
          {showSsh && (
            <button
              type="button"
              class={`fab-cluster-item utb-icon-btn${sshConnected ? ' ssh-connected' : ''}`}
              onClick={handleSshClick}
              title={sshConnected ? 'Disconnect SSH' : 'Connect to remote'}
              aria-label={sshConnected ? 'Disconnect SSH' : 'Connect to remote'}
            >{sshConnected ? '\u23CF' : '\u{1F5A5}'}</button>
          )}
        </div>
      )}

      {/* Floating keyboard panel — appears below the cluster when toggled */}
      {keyboardStripOpen && keyboardContent && (
        <div class="fab-keyboard-panel">
          {keyboardContent}
        </div>
      )}
    </div>
  );
}
